import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { getAntenne, createAntenne, updateAntenne, getEtats, getLieuTypes, getSecteurs, getPays, getDebitInternets, setAuthToken } from '../services/api';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import './AntenneForm.css';

function AntenneForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookups, setLookups] = useState({
    etats: [],
    lieuTypes: [],
    secteurs: [],
    pays: [],
    debits: []
  });

  const [formData, setFormData] = useState({
    lieu_type_id: 1,
    ville_principale: '',
    ville_exacte: '',
    id_wp: '',
    adresse: '',
    cp: '',
    email_commercial: '',
    telephone: '',
    longitude: '',
    latitude: '',
    precision_lieu: '',
    commentaire: '',
    etat_id: 1,
    debit_internet_id: '',
    info_debit: '',
    horaire_accueil: '',
    horaire_dispos: '',
    fond_vert: false,
    statut: true,
    convention_signe: false,
    pays_id: 5,
    secteur_geographique_id: ''
  });

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadLookups();
      if (isEdit) {
        loadAntenne();
      } else {
        setLoading(false);
      }
    }
  }, [auth.user, id]);

  const loadLookups = async () => {
    try {
      const [etats, lieuTypes, secteurs, pays, debits] = await Promise.all([
        getEtats(),
        getLieuTypes(),
        getSecteurs(),
        getPays(),
        getDebitInternets()
      ]);
      setLookups({ etats, lieuTypes, secteurs, pays, debits });
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  const loadAntenne = async () => {
    try {
      const data = await getAntenne(id);
      setFormData({
        lieu_type_id: data.lieu_type_id || 1,
        ville_principale: data.ville_principale || '',
        ville_exacte: data.ville_exacte || '',
        id_wp: data.id_wp || '',
        adresse: data.adresse || '',
        cp: data.cp || '',
        email_commercial: data.email_commercial || '',
        telephone: data.telephone || '',
        longitude: data.longitude || '',
        latitude: data.latitude || '',
        precision_lieu: data.precision_lieu || '',
        commentaire: data.commentaire || '',
        etat_id: data.etat_id || 1,
        debit_internet_id: data.debit_internet_id || '',
        info_debit: data.info_debit || '',
        horaire_accueil: data.horaire_accueil || '',
        horaire_dispos: data.horaire_dispos || '',
        fond_vert: data.fond_vert || false,
        statut: data.statut !== false,
        convention_signe: data.convention_signe || false,
        pays_id: data.pays_id || 5,
        secteur_geographique_id: data.secteur_geographique_id || ''
      });
    } catch (error) {
      console.error('Error loading antenne:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.ville_principale) {
      toast.error('La ville principale est obligatoire');
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await updateAntenne(id, formData);
        toast.success('Antenne mise à jour');
      } else {
        const result = await createAntenne(formData);
        toast.success('Antenne créée');
        navigate(`/antennes/${result.id}`);
        return;
      }
      navigate(`/antennes/${id}`);
    } catch (error) {
      console.error('Error saving antenne:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="antenne-form">
      <header className="page-header">
        <div className="header-info">
          <Link to={isEdit ? `/antennes/${id}` : '/antennes'} className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Retour
          </Link>
          <h1>{isEdit ? 'Modifier une antenne' : 'Nouvelle antenne'}</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="form-container">
        {/* Type */}
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lieu_type_id">Type *</label>
              <select
                id="lieu_type_id"
                name="lieu_type_id"
                value={formData.lieu_type_id}
                onChange={handleChange}
              >
                {lookups.lieuTypes.map(lt => (
                  <option key={lt.id} value={lt.id}>{lt.nom}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Informations */}
        <div className="form-section">
          <h2 className="section-title">Informations</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ville_principale">Ville Principale *</label>
              <input
                type="text"
                id="ville_principale"
                name="ville_principale"
                value={formData.ville_principale}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="adresse">Adresse</label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
              />
            </div>
            <div className="form-group form-group-sm">
              <label htmlFor="cp">CP</label>
              <input
                type="text"
                id="cp"
                name="cp"
                value={formData.cp}
                onChange={handleChange}
              />
            </div>
            {/* Champ « Ville exacte » retiré du formulaire à la demande du client.
                La colonne reste en base et alimentée par les imports : on ne la
                saisit simplement plus à la main. */}
            <div className="form-group">
              <label htmlFor="id_wp">Identifiant page WordPress</label>
              <input
                type="text"
                id="id_wp"
                name="id_wp"
                value={formData.id_wp}
                onChange={handleChange}
                placeholder="Identifiant de la page WordPress de cette antenne"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pays_id">Pays</label>
              <select
                id="pays_id"
                name="pays_id"
                value={formData.pays_id}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                {lookups.pays.map(p => (
                  <option key={p.id} value={p.id}>{p.name_fr}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="secteur_geographique_id">Secteur Géographique</label>
              <select
                id="secteur_geographique_id"
                name="secteur_geographique_id"
                value={formData.secteur_geographique_id}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                {lookups.secteurs.map(s => (
                  <option key={s.id} value={s.id}>{s.nom}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="email_commercial">E-mail commercial</label>
              <input
                type="email"
                id="email_commercial"
                name="email_commercial"
                value={formData.email_commercial}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="telephone">Téléphone</label>
              <input
                type="text"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Localisation */}
        <div className="form-section">
          <h2 className="section-title">Localisation Google Maps</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="latitude">Latitude</label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                placeholder="ex: 48.8566"
              />
            </div>
            <div className="form-group">
              <label htmlFor="longitude">Longitude</label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                placeholder="ex: 2.3522"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="precision_lieu">Précisions sur le lieu</label>
            <textarea
              id="precision_lieu"
              name="precision_lieu"
              value={formData.precision_lieu}
              onChange={handleChange}
              rows="3"
            />
          </div>
        </div>

        {/* Statut */}
        <div className="form-section">
          <h2 className="section-title">Statut</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="etat_id">État</label>
              <select
                id="etat_id"
                name="etat_id"
                value={formData.etat_id}
                onChange={handleChange}
              >
                {lookups.etats.map(e => (
                  <option key={e.id} value={e.id}>{e.valeur}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="debit_internet_id">Débit Internet</label>
              <select
                id="debit_internet_id"
                name="debit_internet_id"
                value={formData.debit_internet_id}
                onChange={handleChange}
              >
                <option value="">Sélectionner</option>
                {lookups.debits.map(d => (
                  <option key={d.id} value={d.id}>{d.nom}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="statut"
                checked={formData.statut}
                onChange={handleChange}
              />
              <span>Actif</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="convention_signe"
                checked={formData.convention_signe}
                onChange={handleChange}
              />
              <span>Convention signée</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="fond_vert"
                checked={formData.fond_vert}
                onChange={handleChange}
              />
              <span>Fond vert disponible</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <Link to={isEdit ? `/antennes/${id}` : '/antennes'} className="btn btn-secondary">
            Annuler
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AntenneForm;
